import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {runUserSearch} from 'src/openapi/admin/apis/UserSearchApi';
import {useAppDispatch} from 'src/store/hooks';

import {userSearchResultStateSelector} from '../slices/user-search-result-state.slice';
import {
    userSearchResultActions,
    userSearchResultDataSelector,
    userSearchResultResultSelector,
} from '../slices/user-search-result.slice';
import {getSearchFilteringCriteria} from '../utils';

import type {UserSearchResultCacheKeys} from '../typedef';
import type {UserSearchCriteria} from 'src/openapi/admin/models/UserSearchCriteria';
import type {UserSearchFiltering} from 'src/openapi/admin/models/UserSearchFiltering';
import type {UserSearchResponse} from 'src/openapi/admin/models/UserSearchResponse';


export const useGetUserSearchResult = () => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback((cacheKeys: UserSearchResultCacheKeys) => apiDispatch(runUserSearch(
        {
            userSearchRequest: {
                ...getSearchFilteringCriteria(
                    cacheKeys.searchCriteria as UserSearchCriteria,
                    cacheKeys.filters as UserSearchFiltering,
                ),
                userSortCriteria: {
                    columnId: cacheKeys.sortCriteria.field,
                    direction: cacheKeys.sortCriteria.direction,
                },
                pagination: cacheKeys.pagination,
            },
        },
    )), [apiDispatch]);

    const setLoadedData = useCallback((
        response: UserSearchResponse,
        cacheKeys: UserSearchResultCacheKeys,
    ) => {
        dispatch(userSearchResultActions.onSuccess(({
            cacheKeys,
            totalCount: response.totalCount,
            data: response.result,
        })));
    }, [dispatch]);

    return useCachedTable({
        dataSelector: userSearchResultDataSelector,
        resultSelector: userSearchResultResultSelector,
        stateSelector: userSearchResultStateSelector,
        actions: userSearchResultActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
